import React, { useCallback, useEffect } from 'react';

import { addSentryBreadcrumb } from '@paragon/client-tools';
import { assertExhaustiveSwitch, resolveHotkeyAction } from '@paragon/utilities';

import { useAppMonitor } from '../../monitor';
import { DEFAULT_KEYBINDINGS, getHotkeyActionResolver } from '../../store/selectors/keyboardSelectors';
import { useAppDispatch, useAppSelector } from '../hooks';

export const KeyboardListener = () => {
  const dispatch = useAppDispatch();

  const monitor = useAppMonitor();

  const hotkeyActionResolver = useAppSelector(getHotkeyActionResolver);

  const onKeyUpOrDown = useCallback(
    async (event: KeyboardEvent) => {
      const resolvedAction = resolveHotkeyAction(event, DEFAULT_KEYBINDINGS, hotkeyActionResolver);
      switch (resolvedAction.result) {
        case 'noAction':
          return;
        case 'multipleActions':
          console.error('Multiple actions associated with triggered hotkey');
          return;
        case 'singleAction':
          event.stopPropagation();
          event.preventDefault();
          const { type, resolution: action } = resolvedAction.action;
          addSentryBreadcrumb({ category: 'Keyboard Hotkey', message: type, level: 'info' });
          monitor.recordInteraction('Hotkey keybinding triggered', { actionType: type });
          await dispatch(action);
          return;
        default:
          assertExhaustiveSwitch(resolvedAction);
      }
    },
    [dispatch, monitor, hotkeyActionResolver],
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyUpOrDown);
    document.addEventListener('keyup', onKeyUpOrDown);
    return () => {
      document.removeEventListener('keydown', onKeyUpOrDown);
      document.removeEventListener('keyup', onKeyUpOrDown);
    };
  }, [onKeyUpOrDown]);

  return <div style={{ display: 'none' }} />;
};
